import { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function OnPageCheckerPage() {
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!url.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/audit/onpage', { url, keyword });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">On Page SEO Checker</h1>
        <p className="page-sub">Get actionable ideas to optimize page for target keyword — like SEMrush On Page Checker · 3 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 10 }}>
          <input className="input" placeholder="Page URL e.g. https://yoursite.com/best-seo-tools" value={url} onChange={e => setUrl(e.target.value)} />
          <input className="input" placeholder="Target keyword e.g. best seo tools" value={keyword} onChange={e => setKeyword(e.target.value)} />
          <button className="btn btn-orange" onClick={analyze} disabled={loading || !url.trim()}>{loading ? 'Checking...' : 'Check →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, marginBottom: 16 }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280', marginBottom: 8 }}>SEO Score</div>
              <div style={{ width: 80, height: 80, borderRadius: '50%', border: `4px solid ${data.score > 80 ? '#00C853' : data.score > 60 ? '#FFAB00' : '#FF3D57'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontWeight: 800, fontSize: 24, color: data.score > 80 ? '#00C853' : data.score > 60 ? '#FFAB00' : '#FF3D57' }}>{data.score}</div>
              <div className="metric-sub">{data.url.slice(0,30)}...</div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Optimization Ideas ({data.recommendations.length})</span></div>
              {data.recommendations.map((r,i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                  <span style={{ color: '#00C853', fontWeight: 800 }}>✓</span>{r}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">On-Page Checks</span></div>
              {data.checks.map((c,i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                  <span>{c.name}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#6B7280', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.value}</span>
                    <span className={`badge ${c.status === 'good' ? 'badge-green' : c.status === 'warning' ? 'badge-yellow' : 'badge-red'}`} style={{ fontSize: 10 }}>{c.status}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Competitors for "{data.keyword}"</span></div>
              {data.competitors.map((comp,i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 }}>
                  <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{comp.url}</span>
                  <span><span className="badge badge-gray">{comp.score} score</span> <span style={{ color: '#6B7280' }}>{comp.wordCount} words</span></span>
                </div>
              ))}
              <div style={{ marginTop: 12, padding: 10, background: '#FFF8E1', borderRadius: 6, fontSize: 12, color: '#8B5E00' }}>
                💡 Tip: Your competitors have avg {Math.floor(data.competitors.reduce((a,b)=>a+b.wordCount,0)/data.competitors.length)} words. Make your content longer and more comprehensive.
              </div>
            </div>
          </div>

          {data.issues.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-header"><span className="card-title">Issues Found ({data.issues.length})</span></div>
              {data.issues.map((iss,i) => (
                <div key={i} style={{ padding: '12px', background: iss.type === 'error' ? '#FFEBEE' : '#FFF8E1', borderRadius: 6, marginBottom: 8, border: `1px solid ${iss.type === 'error' ? '#FFCDD2' : '#FFECB3'}` }}>
                  <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}><span className={`badge ${iss.type === 'error' ? 'badge-red' : 'badge-yellow'}`}>{iss.type}</span>{iss.title} <span className="badge badge-gray">{iss.impact}</span></div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{iss.description}</div>
                  <div style={{ fontSize: 12, marginTop: 6, background: 'white', padding: '6px 10px', borderRadius: 4, border: '1px solid #E5E7EB' }}><strong>How to fix:</strong> {iss.howToFix}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
